import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Drawer, Input, Select, Typography } from 'antd';
import { Pencil, Copy, Trash2, Plus, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { COMPETENCIES, COMPETENCY_BY_ID, QuestionType, type CompetencySlug } from '@evaltick/shared';

import { BuilderTable, type BuilderTableColumn } from '@/components/BuilderTable';
import { BuilderChip } from '@/components/BuilderChip';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import { QuestionForm, type QuestionFormValues } from '@/components/QuestionForm';
import { QuestionDetailDrawer } from '@/components/QuestionDetailDrawer';
import { DrawerHeader } from '@/components/DrawerHeader';
import { BaseAPIClient, QuestionsService } from '@/services/api';
import type { QuestionWithQuestionSets } from '@/services/api';
import { competencyChipColor, typeChipColor } from '@/utils/builderChipColors';
import { formatDate } from '@/utils/formatters';
import { QUESTION_TYPE_LABELS } from '@/utils/questionTypeLabels';
import { ROUTES, buildRoute } from '@/routes';
import { useDrawerSurface } from '@/hooks';
import styles from './AdminQuestions.module.scss';

const { Title, Text } = Typography;

const ALL = 'All' as const;

const COMPETENCY_FILTER_OPTIONS = [
  { label: 'All competencies', value: ALL },
  ...COMPETENCIES.map((c) => ({ label: c.name, value: c.id })),
];

const TYPE_FILTER_OPTIONS = [
  { label: 'All types', value: ALL },
  ...Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => ({ label, value })),
];

const competencyName = (competencyId: string) => COMPETENCY_BY_ID[competencyId]?.name ?? competencyId;

const GRID_TEMPLATE_COLUMNS =
  'minmax(220px, 2.2fr) minmax(150px, 2.3fr) minmax(110px, 0.9fr) minmax(90px, 0.9fr) minmax(110px, 0.85fr) 130px';

const PER_PAGE = 20;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
};

export const AdminQuestions: React.FC = () => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const drawerSurface = useDrawerSurface();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<QuestionWithQuestionSets[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [competencyFilter, setCompetencyFilter] = useState<CompetencySlug | typeof ALL>(ALL);
  const [typeFilter, setTypeFilter] = useState<QuestionType | typeof ALL>(ALL);
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithQuestionSets | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState<QuestionWithQuestionSets | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new QuestionsService(token);
  }, [getToken]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const svc = await getService();
      setQuestions(await svc.list());
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getService, notification]);

  useEffect(() => {
    void fetchQuestions();
  }, [fetchQuestions]);

  const filteredQuestions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return questions.filter((q) => {
      if (competencyFilter !== ALL && q.competencyId !== competencyFilter) return false;
      if (typeFilter !== ALL && q.questionType !== typeFilter) return false;
      if (!term) return true;
      return competencyName(q.competencyId).toLowerCase().includes(term) || q.text.toLowerCase().includes(term);
    });
  }, [questions, search, competencyFilter, typeFilter]);

  const openCreateDrawer = () => {
    setEditingQuestion(null);
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (question: QuestionWithQuestionSets) => {
    setViewingQuestion(null);
    setEditingQuestion(question);
    setIsDrawerOpen(true);
  };

  const openDetailDrawer = (question: QuestionWithQuestionSets) => {
    setViewingQuestion(question);
  };

  const handleSubmit = async (values: QuestionFormValues) => {
    try {
      const svc = await getService();
      if (editingQuestion) {
        await svc.update(editingQuestion.id, values);
        notification.success({ message: 'Question updated' });
      } else {
        await svc.create(values);
        notification.success({ message: 'Question added' });
      }
      setIsDrawerOpen(false);
      await fetchQuestions();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
      throw err; // keep the drawer open and preserve the entered values
    }
  };

  const handleDuplicate = async (question: QuestionWithQuestionSets) => {
    try {
      const svc = await getService();
      await svc.create({
        competencyId: question.competencyId,
        questionType: question.questionType,
        text: question.text,
      });
      notification.success({ message: 'Question duplicated' });
      await fetchQuestions();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const handleDelete = async (question: QuestionWithQuestionSets) => {
    setDeletingId(question.id);
    try {
      const svc = await getService();
      await svc.remove(question.id);
      notification.success({ message: 'Question deleted' });
      setIsDrawerOpen(false);
      await fetchQuestions();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setDeletingId(null);
    }
  };

  const goToQuestionSet = (id: string) => navigate(buildRoute(ROUTES.ADMIN_QUESTION_SET_EDIT, { id }));

  const confirmDeleteQuestion = (question: QuestionWithQuestionSets) => {
    const inUse = question.questionSets.length > 0;
    const instance = modal.confirm({
      title: 'Delete question?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: inUse ? (
        <div className={styles.deleteHint}>
          <div>Remove it from these question sets before it can be deleted:</div>
          <div className={styles.questionSetRefs}>
            {question.questionSets.map((set) => (
              <BuilderChip
                key={set.id}
                tint="purple"
                onClick={() => {
                  instance.destroy();
                  goToQuestionSet(set.id);
                }}
              >
                {set.name}
              </BuilderChip>
            ))}
          </div>
        </div>
      ) : (
        "This can't be undone."
      ),
      okText: 'Delete',
      okButtonProps: { danger: true, disabled: inUse },
      onOk: () => handleDelete(question),
    });
  };

  const columns: BuilderTableColumn<QuestionWithQuestionSets>[] = [
    {
      key: 'text',
      title: 'Question',
      sortFn: (a, b) => a.text.localeCompare(b.text),
      render: (q) => (
        <div className={styles.questionCell} title={q.text}>
          {q.text}
        </div>
      ),
    },
    {
      key: 'competency',
      title: 'Competency',
      sortFn: (a, b) => competencyName(a.competencyId).localeCompare(competencyName(b.competencyId)),
      render: (q) => {
        const name = competencyName(q.competencyId);
        const tint = competencyChipColor(q.competencyId);
        return (
          <BuilderChip tint={tint} title={name}>
            {name}
          </BuilderChip>
        );
      },
    },
    {
      key: 'type',
      title: 'Type',
      sortFn: (a, b) => a.questionType.localeCompare(b.questionType),
      render: (q) => {
        const tint = typeChipColor(q.questionType);
        return (
          <BuilderChip tint={tint}>
            {QUESTION_TYPE_LABELS[q.questionType]}
          </BuilderChip>
        );
      },
    },
    {
      key: 'usedIn',
      title: 'Used in',
      sortFn: (a, b) => a.questionSets.length - b.questionSets.length,
      render: (q) =>
        q.questionSets.length > 0 ? (
          <Text type="secondary" className={styles.usedInCount}>
            {q.questionSets.length} set{q.questionSets.length > 1 ? 's' : ''}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      key: 'updated',
      title: 'Updated',
      sortFn: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (q) => <span className={styles.cellDate}>{formatDate(q.updatedAt)}</span>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (q) => (
        <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
          <BuilderIconButton icon={<Pencil style={{ width: '1em', height: '1em' }} />} title="Edit" onClick={() => openEditDrawer(q)} />
          <BuilderIconButton icon={<Copy style={{ width: '1em', height: '1em' }} />} title="Duplicate" onClick={() => handleDuplicate(q)} />
          <BuilderIconButton
            icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
            title="Delete"
            danger
            onClick={() => confirmDeleteQuestion(q)}
          />
        </div>
      ),
    },
  ];

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.pageHead} variants={itemVariants}>
        <div>
          <Title level={2} className={styles.pageTitle}>
            Question Bank
          </Title>
          <Text className={styles.pageSub}>
            Manage the interview questions available for each competency. Each question is a
            competency + type + prompt.
          </Text>
        </div>
        <Button type="primary" icon={<Plus style={{ width: '1em', height: '1em' }} />} className={styles.btnPrimary} onClick={openCreateDrawer}>
          Add Question
        </Button>
      </motion.div>

      <motion.div className={styles.toolbar} variants={itemVariants}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            placeholder="Search by competency or question…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <Select
          value={competencyFilter}
          onChange={setCompetencyFilter}
          options={COMPETENCY_FILTER_OPTIONS}
          showSearch
          optionFilterProp="label"
          className={styles.filterSelect}
          popupMatchSelectWidth={false}
        />
        <Select
          value={typeFilter}
          onChange={setTypeFilter}
          options={TYPE_FILTER_OPTIONS}
          className={styles.filterSelect}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
      <BuilderTable<QuestionWithQuestionSets>
        columns={columns}
        gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
        data={filteredQuestions}
        rowKey={(q) => q.id}
        pageSize={PER_PAGE}
        loading={loading}
        mobileActionsLabel={(q) => `Actions for ${q.text}`}
        onMobileDetails={(q) => setViewingQuestion(q)}
        defaultSortKey="updated"
        onRowClick={openDetailDrawer}
        emptyTitle={questions.length ? 'No questions match' : 'No questions yet'}
        emptySub={questions.length ? 'Adjust the filters or search.' : 'Use "Add Question" to add the first one to the bank.'}
      />
      </motion.div>

      <Drawer
        title={
          <DrawerHeader
            title={editingQuestion ? 'Edit Question' : 'Create Question'}
            onClose={() => setIsDrawerOpen(false)}
          />
        }
        closable={false}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        <QuestionForm
          initialValues={editingQuestion ?? undefined}
          affectedQuestionSets={editingQuestion?.questionSets ?? []}
          meta={editingQuestion ?? undefined}
          onSubmit={handleSubmit}
          onDelete={editingQuestion ? () => confirmDeleteQuestion(editingQuestion) : undefined}
          deleting={deletingId === editingQuestion?.id}
        />
      </Drawer>

      <Drawer
        title={<DrawerHeader title="Question" onClose={() => setViewingQuestion(null)} />}
        closable={false}
        open={Boolean(viewingQuestion)}
        onClose={() => setViewingQuestion(null)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        {viewingQuestion && (
          <QuestionDetailDrawer
            question={viewingQuestion}
            onEdit={() => openEditDrawer(viewingQuestion)}
            onDuplicate={() => {
              setViewingQuestion(null);
              void handleDuplicate(viewingQuestion);
            }}
            onDelete={() => {
              setViewingQuestion(null);
              confirmDeleteQuestion(viewingQuestion);
            }}
            onNavigateToQuestionSet={goToQuestionSet}
          />
        )}
      </Drawer>
    </motion.div>
  );
};

export default AdminQuestions;
